/**
 * Calendar Utility Functions
 * Shared helper functions for calendar operations
 */

import { MealType, AssignmentMap, MealAssignment } from '../types/calendar'

/**
 * Generate unique assignment key from date and meal type
 */
export function getAssignmentKey(date: string, mealType: MealType): string {
  return `${date}_${mealType}`
}

/**
 * Parse assignment key back into date and meal type
 */
export function parseAssignmentKey(key: string): { date: string; mealType: MealType } | null {
  const parts = key.split('_')
  if (parts.length !== 2) return null

  const date = parts[0]
  const mealType = parts[1]
  if (!date || !mealType || !isValidMealType(mealType)) return null

  return { date, mealType: mealType as MealType }
}

/**
 * Check if a string is a valid meal type
 */
export function isValidMealType(value: string): value is MealType {
  return ['breakfast', 'lunch', 'dinner', 'snacks', 'dessert'].includes(value)
}

/**
 * Get assignment for a specific date and meal type
 */
export function getAssignment(
  assignments: AssignmentMap,
  date: string,
  mealType: MealType
): MealAssignment | null {
  const key = getAssignmentKey(date, mealType)
  return assignments.get(key) || null
}

/**
 * Check if a recipe is assigned to any date
 */
export function isRecipeAssigned(
  assignments: AssignmentMap,
  recipeId: string
): boolean {
  for (const assignment of assignments.values()) {
    if (assignment.recipeId === recipeId) {
      return true
    }
  }
  return false
}

/**
 * Get all dates a recipe is assigned to
 */
export function getRecipeAssignedDates(
  assignments: AssignmentMap,
  recipeId: string
): string[] {
  const dates: string[] = []

  for (const assignment of assignments.values()) {
    if (assignment.recipeId === recipeId) {
      dates.push(assignment.date)
    }
  }

  return dates
}

/**
 * Format date as YYYY-MM-DD in local timezone
 */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Parse date key back to Date object (at midnight local time)
 */
export function parseDateKey(dateKey: string): Date {
  const parts = dateKey.split('-').map(Number)
  if (parts.length !== 3 || parts.some(isNaN)) {
    throw new Error(`Invalid date key format: ${dateKey}`)
  }
  const [year, month, day] = parts
  return new Date(year!, month! - 1, day!, 0, 0, 0, 0)
}

/**
 * Check if a date is today (uses local timezone)
 */
export function isToday(date: Date): boolean {
  const today = getTodayLocal()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

/**
 * Check if a date is editable based on business rules
 * - Current month: fully editable
 * - 1 week prior: editable
 * - 3 months ahead: editable
 * - Beyond that: read-only
 */
export function isDateEditable(date: Date): boolean {
  const today = getTodayLocal()

  const oneWeekAgo = new Date(today)
  oneWeekAgo.setDate(today.getDate() - 7)

  const threeMonthsAhead = new Date(today)
  threeMonthsAhead.setMonth(today.getMonth() + 3)

  const targetDate = new Date(date)
  targetDate.setHours(0, 0, 0, 0)

  return targetDate >= oneWeekAgo && targetDate <= threeMonthsAhead
}

/**
 * Get date range for calendar queries
 * Returns start and end dates for loading data
 */
export function getCalendarDateRange(_weekOffset: number = 0): {
  start: Date
  end: Date
} {
  const today = getTodayLocal()

  // Start from 1 month before current view
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)

  // End at 4 months ahead (1 month past the 3-month editable limit for pre-loading)
  const end = new Date(today.getFullYear(), today.getMonth() + 4, 0)

  return { start, end }
}

/**
 * Get dates for a week view (Sunday to Saturday)
 * Centered on the current week, not today + 6 days
 */
export function getWeekDates(weekOffset: number = 0): Date[] {
  const today = getTodayLocal()

  // Find the Sunday of the current week (0 = Sunday, 6 = Saturday)
  const dayOfWeek = today.getDay()
  const sundayOfWeek = new Date(today)
  sundayOfWeek.setDate(today.getDate() - dayOfWeek)

  // Apply week offset (0 = current week, -1 = previous week, +1 = next week)
  const startDate = new Date(sundayOfWeek)
  startDate.setDate(sundayOfWeek.getDate() + (weekOffset * 7))

  // Generate 7 days starting from Sunday
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + index)
    return date
  })
}

/**
 * Get week offset for a target date relative to today
 */
export function getWeekOffset(targetDate: Date): number {
  const today = getTodayLocal()

  const currentWeekSunday = new Date(today)
  currentWeekSunday.setDate(today.getDate() - today.getDay())
  currentWeekSunday.setHours(0, 0, 0, 0)

  const target = new Date(targetDate)
  target.setHours(0, 0, 0, 0)
  const targetWeekSunday = new Date(target)
  targetWeekSunday.setDate(target.getDate() - target.getDay())
  targetWeekSunday.setHours(0, 0, 0, 0)

  // Math.round absorbs ±1s DST skew on spring-forward/fall-back weeks
  return Math.round(
    (targetWeekSunday.getTime() - currentWeekSunday.getTime()) / (7 * 24 * 60 * 60 * 1000)
  )
}

/**
 * Capitalize first letter of a string
 */
export function capitalizeFirst(str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Get current date at local midnight (single source of truth for "today")
 */
export function getTodayLocal(): Date {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

/**
 * Format date key to human-readable display format
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted string like "Wednesday, Oct 29"
 *
 * @example
 * formatDisplayDate('2025-10-29') // "Wednesday, Oct 29"
 * formatDisplayDate('2025-10-27') // "Monday, Oct 27"
 */
export function formatDisplayDate(dateStr: string): string {
  // Use parseDateKey to ensure local timezone parsing
  const date = parseDateKey(dateStr)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  })
}
